import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle, Sparkles, TrendingUp, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer } from "recharts";
import { APP_NAME, APP_DOMAIN } from "@/lib/branding";

const SPARK_DATA = [
  { v: 42 }, { v: 48 }, { v: 45 }, { v: 55 }, { v: 61 },
  { v: 58 }, { v: 67 }, { v: 74 }, { v: 71 }, { v: 82 }, { v: 89 }, { v: 95 },
];

export default function Hero() {
  return (
    <section className="relative pt-28 lg:pt-32 pb-20 lg:pb-28 overflow-hidden">
      {/* Background grid + radial spotlight */}
      <div
        className="absolute inset-0 -z-10 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(#E2E8F0 1px, transparent 1px), linear-gradient(90deg, #E2E8F0 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse 80% 60% at 50% 0%, #000 40%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 80% 60% at 50% 0%, #000 40%, transparent 100%)",
        }}
      />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 -z-10 h-[500px] w-[900px] rounded-full bg-gradient-to-b from-indigo-100/50 via-white/0 to-transparent blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Eyebrow */}
        <div className="flex justify-center mb-8">
          <div
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full ring-1 ring-indigo-200 bg-indigo-50/80 backdrop-blur-sm"
            data-testid="hero-eyebrow"
          >
            <Sparkles className="h-3.5 w-3.5 text-[#4F46E5]" />
            <span className="text-[12px] font-semibold text-[#4338CA]">
              {APP_NAME} 3.0 · Plateforme de gestion financière intégrée
            </span>
          </div>
        </div>

        {/* Headline */}
        <h1
          className="text-center text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-[#0B0F17] max-w-4xl mx-auto"
          data-testid="hero-headline"
        >
          Pilotez votre entreprise{" "}
          <span className="relative inline-block">
            <span className="relative z-10 bg-gradient-to-br from-[#4F46E5] via-[#4338CA] to-[#059669] bg-clip-text text-transparent">
              avec la précision
            </span>
            <span className="absolute inset-x-0 bottom-1 h-3 bg-indigo-100/60 -z-0" />
          </span>{" "}
          des meilleurs DAF.
        </h1>

        <p
          className="mt-6 text-center text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed"
          data-testid="hero-subtitle"
        >
          Ventes, achats, trésorerie, comptabilité, contrats, RH, flotte, juridique.
          Tout ce qu'un CFO surveille — enfin réuni, connecté à vos banques et
          alimenté par l'IA.
        </p>

        {/* CTAs */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/register?plan=business"
            className="group inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#0B0F17] text-white text-sm font-semibold shadow-lg shadow-slate-900/10 hover:bg-[#1E293B] hover:shadow-slate-900/20 transition-all"
            data-testid="hero-cta-primary"
          >
            Essayer gratuitement 14 jours
            <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <a
            href="#tour"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-slate-700 hover:text-[#0B0F17] text-sm font-semibold border border-slate-200 bg-white hover:border-slate-300 transition-all"
            data-testid="hero-cta-secondary"
          >
            <PlayCircle className="h-4 w-4" />
            Voir la démo (2 min)
          </a>
        </div>

        {/* Trust bar */}
        <div className="mt-8 flex items-center justify-center gap-3 text-xs text-slate-500 flex-wrap">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            +2 500 entreprises · 400 cabinets
          </span>
          <span className="text-slate-300">·</span>
          <span>Aucune CB requise</span>
          <span className="text-slate-300">·</span>
          <span>Migration Pennylane / Sage assistée</span>
        </div>

        {/* Product mockup — bento of live KPIs */}
        <div className="mt-16 lg:mt-20 max-w-5xl mx-auto">
          <div
            className="relative rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/[0.08] overflow-hidden"
            data-testid="hero-mockup"
          >
            {/* Top browser bar */}
            <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-slate-100 bg-slate-50/60">
              <div className="h-2.5 w-2.5 rounded-full bg-slate-200" />
              <div className="h-2.5 w-2.5 rounded-full bg-slate-200" />
              <div className="h-2.5 w-2.5 rounded-full bg-slate-200" />
              <div className="ml-3 flex-1 flex justify-center">
                <div className="px-3 py-0.5 rounded text-[10px] font-mono text-slate-400 bg-white border border-slate-100">
                  app.{APP_DOMAIN}/pilotage
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-4 sm:p-6 grid grid-cols-12 gap-3 sm:gap-4 bg-gradient-to-br from-white to-slate-50/40">
              {/* Big KPI card — Cashflow */}
              <div className="col-span-12 sm:col-span-7 rounded-xl border border-slate-200 bg-white p-5 relative overflow-hidden">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                      Trésorerie disponible
                    </div>
                    <div className="mt-1.5 flex items-baseline gap-2">
                      <span
                        className="text-3xl sm:text-4xl font-bold text-[#0B0F17] tabular-nums"
                        style={{ fontFamily: "'JetBrains Mono', monospace" }}
                      >
                        487 213 €
                      </span>
                      <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                        <ArrowUpRight className="h-3 w-3" />
                        +12,4%
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      3 banques agrégées · MàJ il y a 4 min
                    </div>
                  </div>
                </div>
                <div className="h-32 mt-4 -mx-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={SPARK_DATA}>
                      <defs>
                        <linearGradient id="gCash" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#4F46E5" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#4F46E5" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Area
                        type="monotone"
                        dataKey="v"
                        stroke="#4F46E5"
                        strokeWidth={2}
                        fill="url(#gCash)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Right column stack */}
              <div className="col-span-12 sm:col-span-5 grid grid-cols-2 sm:grid-cols-1 gap-3 sm:gap-4">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Factures à encaisser
                  </div>
                  <div
                    className="mt-1 text-2xl font-bold text-[#0B0F17] tabular-nums"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    134 890 €
                  </div>
                  <div className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />7 factures échues
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Runway
                  </div>
                  <div
                    className="mt-1 text-2xl font-bold text-[#0B0F17] tabular-nums"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    18,4 mois
                  </div>
                  <div className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                    <ArrowDownRight className="h-3 w-3 rotate-180" />
                    Confortable
                  </div>
                </div>
              </div>

              {/* Bottom activity row */}
              <div className="col-span-12 rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Activité IA (aujourd'hui)
                  </div>
                  <span className="text-[10px] text-slate-400">temps réel</span>
                </div>
                <div className="space-y-2">
                  {[
                    { t: "OCR facture Fournisseur Aspen", s: "Rapprochée automatiquement", c: "emerald" },
                    { t: "Bulletin de paie · Mars 2026", s: "12 employés extraits · période détectée", c: "indigo" },
                    { t: "Contrat SaaS · Détection renouvellement", s: "Alerte 90j avant échéance", c: "amber" },
                  ].map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2.5">
                        <span
                          className={`h-1.5 w-1.5 rounded-full bg-${r.c}-500`}
                        />
                        <span className="font-medium text-slate-800">{r.t}</span>
                      </div>
                      <span className="text-slate-500 hidden sm:inline">{r.s}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
